import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import {
  useScrollToFirstError,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import type { AppDispatch } from "@/store";
import { createRepair } from "@/store/repairSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { resolveAiBlockedMessage } from "@/constants/clientAccess";
import {
  fetchCategoryManagement,
  upsertCategoryManagement,
} from "@/store/categoryManagementSlice";
import RepairWorkCreateModal, {
  type RepairTargetInfo,
  scopeToTargetType,
} from "@/components/PropertyList/RegisterRepair/RepairWorkCreateModal";
import type { AreaType } from "@ippon/shared-types";

type RepairFormValues = {
  title: string;
  scope: AreaType | "";
  area: string;
  roomNumber: string;
  categories: string[];
  expenditure_type?: string;
  repair_images?: any[];
  estimate_images?: any[];
};

interface Props {
  /** PreviewDrawer / registerModal 埋め込み時のプリセット */
  overridePropertyId?: string;
  embedded?: boolean;
  /** 埋め込み時、作成成功で閉じる用コールバック */
  onCreated?: (createdId: string) => void;
  /** registerModal 埋め込み時の戻る/キャンセル */
  onCancel?: () => void;
  /** 場所区分の初期値 (private / common / facility / land / other) */
  initialScope?: AreaType;
  /** 名称 (area = unit_id) の初期値。対象 Unit が確定している時に渡す */
  initialAreaId?: string;
}

/**
 * 修繕工事 新規作成ページ。
 *
 * ルート: `/repairs/new?property_id=xxx`
 * PreviewDrawer 経由埋め込み時は `overridePropertyId` 優先。
 */
const RepairCreatePage: React.FC<Props> = ({ overridePropertyId, embedded, onCreated, onCancel, initialScope, initialAreaId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const [searchParams] = useSearchParams();
  const propertyId = overridePropertyId || searchParams.get("property_id") || "";

  const { register, control, handleSubmit, reset, setValue, watch, formState: { errors, isDirty } } =
    useForm<RepairFormValues>({
      defaultValues: {
        title: "",
        scope: initialScope ?? "",
        area: initialAreaId ?? "",
        roomNumber: "",
        categories: [],
        expenditure_type: "",
        repair_images: [],
        estimate_images: [],
      },
    });

  const [targetInfo, setTargetInfo] = useState<RepairTargetInfo>(
    initialScope && initialAreaId
      ? { target_type: scopeToTargetType(initialScope), target_id: initialAreaId }
      : {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollToFirstError = useScrollToFirstError<RepairFormValues>();

  const onSubmit = handleSubmit(async (data) => {
    if (!propertyId) {
      alert("物件情報が見つかりません");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: any = {
        property_id: propertyId,
        repair_title: data.title,
        area_type: data.scope,
        area_name:
          // 区画が target_id で参照される (専有部/共用部/敷地/その他) または建物設備の場合は label を使う
          targetInfo.target_id || targetInfo.target_type === "facility"
            ? targetInfo.target_label || data.area
            : data.area,
        status: "未対応",
        category: data.categories || [],
        expenditure_type: data.expenditure_type || "",
        repair_images: data.repair_images || [],
        estimate_images: data.estimate_images || [],
        target_type: targetInfo.target_type,
        target_id: targetInfo.target_id,
      };
      const action: any = await dispatch(createRepair(payload) as any);
      if (createRepair.rejected.match(action)) {
        // 見積書を付けた登録は、AI を止めた 403（トライアルの無料枠を使い切った・解約後の猶予期間）で断られる
        showToast({
          message: resolveAiBlockedMessage(action.payload) ?? "工事の登録に失敗しました",
          type: "error",
        });
        return;
      }
      const created = action?.payload;
      if (created && created.repair_id) {
        // カテゴリ管理の upsert
        const rec = await (dispatch(fetchCategoryManagement()) as any)
          .unwrap()
          .catch(() => null);
        const existing: string[] = Array.isArray(rec?.categories)
          ? rec.categories
          : [];
        const next = Array.from(
          new Set([
            ...(existing || []),
            ...((data.categories as string[]) || []),
          ]),
        ).filter(Boolean);
        if (next.length !== existing.length) {
          await dispatch(
            upsertCategoryManagement({
              existingId: rec?.category_management_id,
              categories: next,
            }) as any,
          )
            .unwrap()
            .catch(() => {});
        }
        reset();
        setTargetInfo({});
        if (embedded) {
          onCreated?.(created.repair_id);
        } else {
          navigate(`/repairs/${created.repair_id}`, {
            state: {
              propertyId: propertyId,
              fromSource: "repairs",
              showToast: { message: "工事を登録しました", type: "success" },
            },
          });
        }
      }
    } catch {
      alert("工事の登録に失敗しました");
    } finally {
      setIsSubmitting(false);
    }
  }, scrollToFirstError);

  const repairForm = (
    <RepairWorkCreateModal
      asPage
      embedded={embedded}
      open
      onClose={embedded ? () => onCancel?.() : () => navigate(-1)}
      control={control as any}
      register={register as any}
      errors={errors as any}
      setValue={(n, v) => setValue(n as any, v)}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      propertyId={propertyId}
      watch={watch as any}
      onTargetChange={(info) => setTargetInfo(info)}
    />
  );

  // 埋め込み時は親ドロワーが閉じ確認を持つ。ページ直開き時のみ遷移ガードを付ける。
  return embedded ? (
    repairForm
  ) : (
    <CustomFormNavigationGuard hasFormChanged={isDirty}>
      {() => <Box>{repairForm}</Box>}
    </CustomFormNavigationGuard>
  );
};

export default RepairCreatePage;
